import { useIntl } from "react-intl";

import { Button } from "~/components/ui/button";
import { BodyCopy } from "~/hec/components/ui/body-copy";
import type { GoalBannerLevel } from "~/hooks/learning-center/transformers";
import { useRootLayoutData } from "~/root";

import messages from "./messages";
import type { GoalBannerEnrollment } from "./types";

/**
 * Port of hec-composable `goal-banner/goalLevelCopy.jsx`. The per-level loyalty
 * copy under the "Level N" heading.
 *
 * Enrollment status comes from the root layout (`customerLoyalty`, parsed from
 * the persisted `c_acLoyaltyData`) + `isRegistered`, mirroring composable's
 * registered/enrolled branches. The exact "Potential Earnings: N Points" value
 * needs the AnnexCloud available-to-earn data, which isn't fetched client-side
 * in Workers yet (see `./types`) — so the enrolled branch shows non-numeric copy
 * until that resource route lands.
 */
const GoalLevelCopy = ({
  level,
  enrollment,
}: {
  level: GoalBannerLevel;
  enrollment: GoalBannerEnrollment;
}) => {
  const intl = useIntl();
  const { isRegistered, customerLoyalty } = useRootLayoutData();
  const isEnrolled = enrollment.isEnrolled || customerLoyalty;

  const loyaltyPoints =
    (level.loyaltyactionId &&
      enrollment.loyaltyData?.find(a => a.actionId === level.loyaltyactionId)
        ?.actionPoints) ||
    null;

  // Not registered, or registered but not loyalty-enrolled → enroll prompt.
  if (!isRegistered || !isEnrolled) {
    return (
      <>
        <a
          href="#login"
          className="inline"
          data-testid="goal-banner-enroll-link"
        >
          <Button
            variant="linkInline"
            className="inline border-0 bg-transparent p-0 underline"
          >
            {intl.formatMessage(messages.enrollNow)}
          </Button>
        </a>
        <BodyCopy
          size="small"
          className="inline"
          data-testid="goal-banner-level-copy"
        >
          {" "}
          {intl.formatMessage(messages.enrollPrompt)}
        </BodyCopy>
      </>
    );
  }

  if (enrollment.isMaxEarned) {
    return (
      <BodyCopy size="small" data-testid="goal-banner-level-copy">
        {intl.formatMessage(messages.maxEarned, {
          points: enrollment.maxEarned,
        })}
      </BodyCopy>
    );
  }

  // Enrolled. Show the numeric earn/earned copy when available-to-earn points
  // are known; otherwise fall back to non-numeric copy (see file header).
  if (loyaltyPoints != null) {
    return (
      <BodyCopy size="small" data-testid="goal-banner-level-copy">
        {level.complete
          ? intl.formatMessage(messages.earnedPoints, { points: loyaltyPoints })
          : intl.formatMessage(messages.potentialEarnings, {
              points: loyaltyPoints,
            })}
      </BodyCopy>
    );
  }

  return (
    <BodyCopy size="small" data-testid="goal-banner-level-copy">
      {level.complete
        ? intl.formatMessage(messages.levelComplete)
        : intl.formatMessage(messages.levelCompletePrompt)}
    </BodyCopy>
  );
};

export default GoalLevelCopy;
